// Registry of per-status visual effects on a card. Each entry maps
// a STATUS_EFFECTS key (see cardMesh.js → STATUS_EFFECTS) to a trio
// of create/update/dispose functions. GameBoard uses this to
// instantiate the right effect when a status is added to a card,
// tick it each frame, and tear it down when the status is removed.
//
// Adding a new effect:
//   1. Build it as src/utils/game/visualEffects/<name>.js, exporting
//      create<Name>Effect(), update<Name>Effect(effect, dt, mesh),
//      and dispose<Name>Effect(effect).
//   2. Each effect's `create*` must return an object with a
//      `.points` field that is a THREE.Object3D the GameBoard can
//      add to the scene root.
//   3. Register the trio here keyed by the status string used in
//      cardInstance.statuses (e.g. 'stealth', 'ward').

import {
  createStealthSmokeEffect,
  updateStealthSmokeEffect,
  disposeStealthSmokeEffect,
} from './stealthSmoke';
import {
  createWardShieldEffect,
  updateWardShieldEffect,
  disposeWardShieldEffect,
} from './wardShield';
import {
  createAirborneWindEffect,
  updateAirborneWindEffect,
  disposeAirborneWindEffect,
} from './airborneWind';
import {
  createLethalSkullEffect,
  updateLethalSkullEffect,
  disposeLethalSkullEffect,
} from './lethalSkull';
import {
  createSubmergeWaterEffect,
  updateSubmergeWaterEffect,
  disposeSubmergeWaterEffect,
} from './submergeWater';
import {
  createChargeLightningEffect,
  updateChargeLightningEffect,
  disposeChargeLightningEffect,
} from './chargeLightning';
import {
  createBurrowingEarthEffect,
  updateBurrowingEarthEffect,
  disposeBurrowingEarthEffect,
} from './burrowingEarth';
import {
  createRangedBowEffect,
  updateRangedBowEffect,
  disposeRangedBowEffect,
} from './rangedBow';
import {
  createSummoningSleepEffect,
  updateSummoningSleepEffect,
  disposeSummoningSleepEffect,
} from './summoningSleep';

export const CARD_STATUS_EFFECTS = {
  // Derived status (hasSummoningSickness), not a `statuses` entry. Listed
  // last in STATUS_EFFECTS so a real keyword's atmospheric effect wins the
  // one-VFX-per-card slot over the sleep glyphs.
  summoning_sick: {
    create: createSummoningSleepEffect,
    update: updateSummoningSleepEffect,
    dispose: disposeSummoningSleepEffect,
  },
  stealth: {
    create: createStealthSmokeEffect,
    update: updateStealthSmokeEffect,
    dispose: disposeStealthSmokeEffect,
  },
  ward: {
    create: createWardShieldEffect,
    update: updateWardShieldEffect,
    dispose: disposeWardShieldEffect,
  },
  airborne: {
    create: createAirborneWindEffect,
    update: updateAirborneWindEffect,
    dispose: disposeAirborneWindEffect,
  },
  lethal: {
    create: createLethalSkullEffect,
    update: updateLethalSkullEffect,
    dispose: disposeLethalSkullEffect,
  },
  submerge: {
    create: createSubmergeWaterEffect,
    update: updateSubmergeWaterEffect,
    dispose: disposeSubmergeWaterEffect,
  },
  charge: {
    create: createChargeLightningEffect,
    update: updateChargeLightningEffect,
    dispose: disposeChargeLightningEffect,
  },
  burrowing: {
    create: createBurrowingEarthEffect,
    update: updateBurrowingEarthEffect,
    dispose: disposeBurrowingEarthEffect,
  },
  ranged: {
    create: createRangedBowEffect,
    update: updateRangedBowEffect,
    dispose: disposeRangedBowEffect,
  },
};
